import React, { useContext, useMemo } from 'react';

import {
  Carousel,
  Colors,
  Flex,
  HEADER_HEIGHT,
  IconChevronRight,
  RemoteImage,
  Typography,
  useSafeArea,
  useScreenSize,
  VerticalSpacing,
} from '@banhala/ui-kit';
import { useMarketBanner } from '../../queries/useMarketBanner';
import type { MarketBanner } from 'shared/src/api';
import Animated, {
  Extrapolation,
  interpolate,
  useAnimatedStyle,
} from 'react-native-reanimated';
import { ScrollContext } from './context/ScrollContext';
import { useLogClickBanner } from './hooks/useLogClickBanner';
import { useGuardedScreenWidth, useOpenDeeplink } from 'shared/src/hooks';
import { ErrorBoundary } from 'shared/src/component';
import { useMarketSno } from '../../hooks/useMarketSno';

const MarketHomeBannerWrapper = () => {
  const { width: screenWidth } = useScreenSize();

  return (
    // 에러 시 배너 안 보여주기
    <ErrorBoundary ErrorFallback={() => <></>}>
      <React.Suspense
        fallback={
          <Flex
            width={screenWidth}
            height={screenWidth}
            backgroundColor={'bg_secondary'}
          ></Flex>
        }
      >
        <MarketHomeBanner />
      </React.Suspense>
    </ErrorBoundary>
  );
};

const MarketHomeBanner = () => {
  const marketId = useMarketSno();
  const screenWidth = useGuardedScreenWidth('MarketHomeBanner');
  const { top } = useSafeArea();

  const { data } = useMarketBanner(marketId);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [isScrolling, setIsScrolling] = React.useState(false);

  const focusedBanner = useMemo(() => {
    if (!data || !data[currentIndex]) return null;

    return data[currentIndex];
  }, [currentIndex, data]);
  const logClickBanner = useLogClickBanner();
  const { openDeeplink } = useOpenDeeplink();

  // const testGroup = useExperimentGroup({
  //   sno: 'EXP1509',
  //   targetGroups: ['B', 'C'],
  // });

  const values = useContext(ScrollContext);

  const animatedStyle = useAnimatedStyle(() => {
    if (!values.scrollY) return {};

    return {
      transform: [
        {
          scale: interpolate(
            values.scrollY.value,
            [0, -300],
            [1, 3],
            Extrapolation.CLAMP,
          ),
        },
      ],
    };
  });

  const viewHeight = useMemo(() => {
    if (!data) return screenWidth;

    if (data.length === 0) {
      // if (testGroup) {
      //   return 0;
      // }
      return HEADER_HEIGHT + top;
    }

    return screenWidth;
  }, [data, screenWidth, top]);

  const backgroundColor = useMemo(() => {
    if (!data) return 'bg_secondary';

    if (data.length === 0) return 'bg_always_white';

    return 'bg_secondary';
  }, [data]);

  if (!screenWidth) return null;

  return (
    <Flex backgroundColor={backgroundColor} height={viewHeight}>
      {data && data.length > 0 && (
        <Animated.View style={[animatedStyle]}>
          <Carousel<MarketBanner>
            data={data ?? []}
            onSnapToItem={setCurrentIndex}
            renderItem={({ item: carouselItem, index }) => (
              <Flex
                key={carouselItem.sno.toString()}
                width={screenWidth}
                height={screenWidth}
                onPress={() => {
                  if (isScrolling) return;
                  logClickBanner({
                    index: index,
                    bannerSno: carouselItem.sno,
                    goodsSno: carouselItem.related_goods_sno,
                  });

                  openDeeplink(carouselItem.deeplink);
                }}
                testID={'마켓홈배너이미지'}
              >
                <RemoteImage
                  uri={carouselItem.image_url}
                  width={screenWidth}
                  height={screenWidth}
                />
              </Flex>
            )}
            width={screenWidth}
            height={screenWidth}
            autoPlay
            autoPlayInterval={3000}
            onScrollStart={() => {
              setIsScrolling(true);
            }}
            onScrollEnd={index => {
              setIsScrolling(false);
              setCurrentIndex(index);
            }}
          />
          <Flex
            position={'absolute'}
            left={0}
            right={0}
            bottom={0}
            top={0}
            backgroundColor={'alpha_dark30'}
            pointerEvents={'none'}
          />
        </Animated.View>
      )}

      {focusedBanner && (
        <Flex.Row
          alignItems={'flex-end'}
          justifyContent={'space-between'}
          position={'absolute'}
          left={24}
          right={24}
          bottom={30}
        >
          <Flex>
            <Flex gap={4}>
              {focusedBanner.titles.map((title, index) => (
                <Typography
                  type={'h3'}
                  color={'content_always_white'}
                  key={`${title}-${index}`}
                >
                  {title}
                </Typography>
              ))}
            </Flex>
            <VerticalSpacing size={12} />
            <Flex.Row alignItems={'center'} gap={2}>
              <Typography type={'subtitle2'} color={'content_always_white'}>
                자세히 보기
              </Typography>
              <IconChevronRight
                width={12}
                height={12}
                fill={Colors['content_always_white']}
              />
            </Flex.Row>
          </Flex>

          <Flex
            backgroundColor={'alpha_dark40'}
            padding={{ vertical: 4, horizontal: 8 }}
            borderRadius={'xlarge'}
          >
            <Typography type={'subtitle1'} color={'content_always_white'}>
              {currentIndex + 1}
              <Typography type={'body1'} color={'content_always_white'}>
                {' '}
                / {data?.length}
              </Typography>
            </Typography>
          </Flex>
        </Flex.Row>
      )}
    </Flex>
  );
};

export default MarketHomeBannerWrapper;
